import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  alpha
} from '@mui/material';
import SettingsDialogBase from './SettingsDialogBase';


// Permission categories - in a real app, these would come from your backend
const permissionGroups = [
  {
    name: 'Dashboard',
    permissions: [
      { id: 'dashboard-view', name: 'View Dashboard', description: 'Access to view the main dashboard' },
      { id: 'dashboard-reports', name: 'View Reports', description: 'Access to view reports and analytics' }
    ]
  },
  {
    name: 'Menu Management',
    permissions: [
      { id: 'menu-view', name: 'View Menu Items', description: 'Can view menu items' },
      { id: 'menu-add', name: 'Add Menu Items', description: 'Can add new menu items' },
      { id: 'menu-edit', name: 'Edit Menu Items', description: 'Can edit existing menu items' },
      { id: 'menu-delete', name: 'Delete Menu Items', description: 'Can delete menu items' }
    ]
  },
  {
    name: 'Orders',
    permissions: [
      { id: 'orders-view', name: 'View Orders', description: 'Can view all orders' },
      { id: 'orders-process', name: 'Process Orders', description: 'Can process and update orders' },
      { id: 'orders-refund', name: 'Issue Refunds', description: 'Can issue refunds for orders' }
    ]
  },
  {
    name: 'POS System',
    permissions: [
      { id: 'pos-access', name: 'Access POS', description: 'Can access the POS system' },
      { id: 'pos-discounts', name: 'Apply Discounts', description: 'Can apply discounts at checkout' },
      { id: 'pos-void', name: 'Void Transactions', description: 'Can void transactions' }
    ]
  },
  {
    name: 'User Management',
    permissions: [
      { id: 'users-view', name: 'View Users', description: 'Can view user list' },
      { id: 'users-add', name: 'Add Users', description: 'Can add new users' },
      { id: 'users-edit', name: 'Edit Users', description: 'Can edit user details' },
      { id: 'users-delete', name: 'Delete Users', description: 'Can delete users' }
    ]
  },
  {
    name: 'Settings',
    permissions: [
      { id: 'settings-view', name: 'View Settings', description: 'Can view system settings' },
      { id: 'settings-edit', name: 'Edit Settings', description: 'Can modify system settings' },
      { id: 'settings-permissions', name: 'Manage Permissions', description: 'Can assign permissions to roles' }
    ]
  }
];

// Role definitions - in a real app, these would come from your backend
const roleDefinitions = [
  { id: 'owner', name: 'Owner', description: 'Full access to all features', editable: false },
  { id: 'manager', name: 'Manager', description: 'Manage day-to-day operations', editable: true },
  { id: 'staff', name: 'Staff', description: 'Front-line employees with limited access', editable: true },
  { id: 'cashier', name: 'Cashier', description: 'POS access and order processing only', editable: true }
];

const RolePermissionsDialog = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rolePermissions, setRolePermissions] = useState({});
  const [saveInProgress, setSaveInProgress] = useState(false);
  const roles = roleDefinitions;
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  
  // Simulate loading initial data
  useEffect(() => {
    const loadRolePermissions = async () => {
      setLoading(true);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Initialize mock permission data for each role
      const mockPermissions = {};
      
      roleDefinitions.forEach(role => {
        // Owners have all permissions
        if (role.id === 'owner') {
          mockPermissions[role.id] = permissionGroups.flatMap(
            group => group.permissions.map(p => p.id)
          );
        }
        // Managers have most permissions except critical ones
        else if (role.id === 'manager') {
          mockPermissions[role.id] = permissionGroups.flatMap(
            group => group.permissions
              .filter(p => !['settings-permissions', 'users-delete'].includes(p.id))
              .map(p => p.id)
          );
        }
        // Staff have basic permissions
        else if (role.id === 'staff') {
          mockPermissions[role.id] = [
            'dashboard-view',
            'menu-view',
            'orders-view',
            'orders-process',
            'pos-access'
          ];
        }
        // Cashiers only have POS permissions
        else if (role.id === 'cashier') {
          mockPermissions[role.id] = [
            'pos-access',
            'orders-view',
            'orders-process'
          ];
        }
      });
      
      setRolePermissions(mockPermissions);
      setLoading(false);
    };
    
    if (open) {
      loadRolePermissions();
    }
  }, [open]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedRole(roles[newValue]);
  };
  
  const handlePermissionChange = (permissionId) => (event) => {
    const checked = event.target.checked;
    const currentPermissions = [...(rolePermissions[selectedRole.id] || [])];
    
    if (checked && !currentPermissions.includes(permissionId)) {
      currentPermissions.push(permissionId);
    } else if (!checked && currentPermissions.includes(permissionId)) {
      const index = currentPermissions.indexOf(permissionId);
      currentPermissions.splice(index, 1);
    }
    
    setRolePermissions({
      ...rolePermissions,
      [selectedRole.id]: currentPermissions
    });
  };
  
  const isPermissionChecked = (permissionId) => {
    return rolePermissions[selectedRole?.id]?.includes(permissionId) || false;
  };
  
  const isPermissionDisabled = () => {
    // Owner role permissions cannot be modified
    return selectedRole?.id === 'owner';
  };
  
  const handleSavePermissions = async () => {
    setSaveInProgress(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In a real app, you'd save the permissions to your backend:
    // await axios.post('/api/v1/roles/permissions', { rolePermissions });
    
    setSaveInProgress(false);
    onClose();
  };
  
  return (
    <SettingsDialogBase
      open={open}
      onClose={onClose}
      title="Role Permissions Configuration"
      description="Configure what each user role can access"
      onSave={handleSavePermissions}
      saveDisabled={selectedRole?.id === 'owner' || saveInProgress}
      size="lg"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3 }}
          >
            {roles.map((role, index) => (
              <Tab
                key={role.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {role.name}
                    {!role.editable && (
                      <Chip 
                        label="System" 
                        size="small" 
                        color="primary" 
                        sx={{ height: 20 }} 
                      />
                    )}
                  </Box>
                }
                id={`role-tab-${index}`}
                aria-controls={`role-tabpanel-${index}`}
              />
            ))}
          </Tabs>
          
          {selectedRole?.id === 'owner' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              The Owner role has full system access and its permissions cannot be modified.
            </Alert>
          )}
          
          <Typography variant="subtitle1" gutterBottom>
            Description: {selectedRole?.description}
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Feature Group</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Access</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissionGroups.map((group) => (
                  <React.Fragment key={group.name}>
                    <TableRow sx={{ backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 600 }}>
                        {group.name}
                      </TableCell>
                    </TableRow>
                    {group.permissions.map((permission) => (
                      <TableRow key={permission.id}>
                        <TableCell></TableCell>
                        <TableCell>{permission.name}</TableCell>
                        <TableCell>{permission.description}</TableCell>
                        <TableCell align="center">
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isPermissionChecked(permission.id)}
                                  onChange={handlePermissionChange(permission.id)}
                                  disabled={isPermissionDisabled()}
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                          </FormGroup>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </SettingsDialogBase>
  );
};

export default RolePermissionsDialog;